<template>
  <Page>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <router-link
            class="btn btn-action btn-action-back"
            :to="`/taxonomy`">
            <i class="fas fa-chevron-left" />
          </router-link>
          <div class="title-container">
            <div class="subtitle">จัดการหมวดหมู่</div>
            <div class="title">{{tagKey}}</div>
          </div>
          <div class="actions">
            <router-link
              :to="`${tagKey}/create`"
              class="btn btn-primary">
              <i class="fas fa-plus icon"></i> สร้างแท็กใหม่
            </router-link>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <PreloaderSection v-if="loadTagState.isLoading" />
      <div v-if="!loadTagState.isLoading">
        <!-- render content -->
        <Section v-if="!loadTagState.error">
          <template v-slot:section-filter>
            <input
              class="form-control form-control-search"
              placeholder="ค้นหาด้วยชื่อแท็ก..."
              @keyup.enter.stop.prevent="onSearch"
            />
          </template>
          <!-- render content -->
          <template v-slot:section-body>
            <div v-if="!isEmpty()">
              <List>
                <template v-slot:list-header>
                  <div class="list-item-detail">
                    <div class="list-item list-item-title">ชื่อแท็ก</div>
                    <div class="list-item">สร้างเมื่อ</div>
                    <div class="list-item list-item-action"></div>
                  </div>
                </template>
                <template v-slot:list-body>
                  <div
                    class="list-item-container no-underline"
                    v-for="item in taxonomy"
                    :key="item.id"
                  >
                    <div class="list-item-detail">
                      <div class="list-item list-item-primary list-item-title">
                        {{item.name}}
                      </div>
                      <div class="list-item">
                        {{formatDateTime(item.createdAt)}}
                      </div>
                      <div class="list-item list-item-action">
                        <div class="action-container">
                          <router-link
                            class="btn btn-light btn-action"
                            :to="`/taxonomy/${tagKey}/${item.id}/edit`"
                          >
                            <i class="fas fa-pencil-alt"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </List>
              <div
                class="pagination-container"
                v-if="isShowPagination()"
              >
                <Pagination :lastPage="lastPage" />
              </div>
            </div>
            <!-- empty list --->
            <EmptyMessage v-if="isEmpty()">
              <h3>ไม่มีข้อมูล</h3>
              <router-link
                class="btn btn-light btn-lg mt-4"
                :to="`${tagKey}/create`"
              >
                สร้างแท็กใหม่
              </router-link>
            </EmptyMessage>
            <!-- search result -->
            <EmptyMessage v-if="isEmptySearch()">
              <h3>ไม่พบข้อความที่ค้นหา</h3>
              <button
                type="button"
                class="btn btn-light btn-lg mt-4"
                @click="clearSearch"
              >
                ล้างการค้นหา
              </button>
            </EmptyMessage>
          </template>
        </Section>
        <!-- error from api -->
        <EmptyMessage v-if="loadTagState.error && !isLoading">
          <h3>เกิดข้อผิดพลาดขณะโหลดข้อมูล</h3>
          <router-link
            :to="`/taxonomy/`"
            class="btn btn-light btn-lg mt-4"
          >
            กลับสู่หน้าแท็ก
          </router-link>
          <span
            slot="code"
            class="code"
          >
            Code : {{loadTagState.error.statusCode || 'not found' }}
          </span>
        </EmptyMessage>
      </div>
    </div>
  </Page>
</template>

<script>
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import Page from '@/layouts/Page.vue';
import Pagination from '@/components/Pagination.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import PreloaderSection from '@/components/PreloaderSection.vue';

import { loadTaxonomyByKey } from './api';

export default {
  mixins: [dateFormetter],
  components: {
    Page,
    Pagination,
    Section,
    List,
    EmptyMessage,
    PreloaderSection,
  },
  data() {
    return {
      taxonomy: null,
      lastPage: null,
      loadTagState: {
        isLoading: true,
        error: null,
      },
    };
  },
  computed: {
    filter() {
      return this.$route.query;
    },
    tagKey() {
      return this.$route.params.tagKey;
    },
  },
  watch: {
    filter(newValue) {
      this.load(newValue);
    },
  },
  created() {
    this.load(this.filter);
  },
  methods: {
    async load() {
      try {
        if (!_.isEmpty(this.tagKey)) {
          const response = await loadTaxonomyByKey(this.tagKey, this.filter);
          this.taxonomy = response.data.data;
          this.lastPage = response.data.lastPage;
          this.loadTagState.isLoading = false;
        }
      } catch (error) {
        this.loadTagState.error = error.response;
        this.loadTagState.isLoading = false;
      }
    },
    isEmptySearch() {
      return _.isEmpty(this.taxonomy) && !_.every(this.filter, _.isEmpty);
    },
    isEmpty() {
      return _.isEmpty(this.taxonomy) && _.every(this.filter, _.isEmpty);
    },
    onSearch(event) {
      const filter = { ...this.filter, q: event.target.value };
      this.$router.push({ query: { ...filter } });
    },
    clearSearch() {
      const filter = null;
      this.$router.push({ query: { ...filter } });
    },
    isShowPagination() {
      return this.lastPage > 1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/form.scss';

@media (max-width: 575.98px) {
  .list-item-container {
    .list-item-detail {
      .list-item {
        font-size: $font-size-base * 0.875;
        display: none;

        &.list-item-primary {
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
          padding: $spacer * 0.25;
        }

        &.list-item-secondary {
          display: flex;
          order: -1;
          padding: $spacer * 0.25;
          font-size: $font-size-base * 0.875;
        }

        &.list-item-status {
          display: flex;
        }
      }
    }
  }
}
</style>
